import { useState, type ComponentType } from 'react';
import type { AnyFieldApi } from '@tanstack/react-form';
import { Input } from '@/components/ui/input';
import { SectionHeading } from '../common/SectionHeading';
import { useTranslation } from '@hooks/useTranslation';
import type { FormFieldRowProps } from '../Form/FormFieldRow';
import { OptionSelect } from '../Form/OptionSelect';
import { ACCESS_LEVEL_VALUES, CONFIRMATION_STATUS_VALUES, EMPLOYMENT_TYPE_VALUES } from '../../constants';

const STAFF_SELECTS = [
  { name: 'confirmation_status', values: CONFIRMATION_STATUS_VALUES, placeholderKey: 'select_confirmation_status' },
  { name: 'access_level', values: ACCESS_LEVEL_VALUES, placeholderKey: 'select_venue_access' },
  { name: 'employment_type', values: EMPLOYMENT_TYPE_VALUES, placeholderKey: undefined },
] as const;

type StaffFieldRowProps = Pick<FormFieldRowProps, 'name' | 'label' | 'marker' | 'markerLabel' | 'children'>;

interface StaffDetailsFieldsProps {
  hidden: boolean;
  FieldRow: ComponentType<StaffFieldRowProps>;
  headingSize: 'sm' | 'md';
  savedNric: string | null;
  className?: string;
}

export function StaffDetailsFields({ hidden, FieldRow, headingSize, savedNric, className }: StaffDetailsFieldsProps) {
  const { t, getSection } = useTranslation();
  const placeholders = getSection<Record<string, string>>('form.placeholders') || {};

  return (
    <div hidden={hidden} className={className}>
      <SectionHeading as="h4" rule={false} size={headingSize}>
        {t('common.sections.staff_details')}
      </SectionHeading>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        {STAFF_SELECTS.map(({ name, values, placeholderKey }) => {
          const label = t(`form.labels.${name}`);
          return (
            <FieldRow key={name} name={name} label={label}>
              {(field, { invalid }) => (
                <OptionSelect
                  field={field}
                  options={values.map((value) => ({ value, label: t(`form.options.${name}.${value}`) }))}
                  label={label}
                  placeholder={placeholderKey && placeholders[placeholderKey]}
                  invalid={invalid}
                />
              )}
            </FieldRow>
          );
        })}

        <FieldRow name="nric" label={t('form.labels.nric')}>
          {(field, { invalid }) => (
            <NricInput field={field} savedNric={savedNric} placeholder={placeholders.nric ?? ''} invalid={invalid} />
          )}
        </FieldRow>

        <FieldRow
          name="vehicle_number"
          label={t('form.labels.vehicle_number')}
          marker="optional"
          markerLabel={t('form.optional')}
        >
          {(field, { invalid }) => (
            <Input
              value={field.state.value as string}
              onChange={(e) => field.handleChange(e.target.value.toUpperCase())}
              onBlur={field.handleBlur}
              placeholder={placeholders.vehicle_number ?? ''}
              autoComplete="off"
              variant="form"
              aria-invalid={invalid || undefined}
            />
          )}
        </FieldRow>
      </div>
    </div>
  );
}

function NricInput({
  field,
  savedNric,
  placeholder,
  invalid,
}: {
  field: AnyFieldApi;
  savedNric: string | null;
  placeholder: string;
  invalid: boolean;
}) {
  const [replacing, setReplacing] = useState(false);
  const value = field.state.value as string;
  const showingSaved = savedNric !== null && !replacing && value === '';

  return (
    <Input
      value={showingSaved ? savedNric : value}
      onFocus={(e) => {
        if (showingSaved) e.target.select();
      }}
      onChange={(e) => {
        setReplacing(true);
        field.handleChange(e.target.value.toUpperCase());
      }}
      onBlur={() => {
        if (value === '') setReplacing(false);
        field.handleBlur();
      }}
      placeholder={placeholder}
      autoComplete="off"
      variant="form"
      aria-invalid={invalid || undefined}
    />
  );
}
